import { memo } from 'react';
import Image from 'next/image';
import { match } from 'ts-pattern';

import type { PublicPredictionMarket } from '../../services/interfaces/market.types';

import { useStyles } from './MarketIcon.styles';

type MarketType = PublicPredictionMarket['type'];

const DEFAULT_SIZE = 32;

type RemoteIconProps = {
  iconUrl?: string;
  size?: number;
  mirrored?: boolean;
  className?: string;
};

type MarketGroupIconProps = Pick<RemoteIconProps, 'iconUrl' | 'size' | 'className'>;

type MarketIconProps = {
  marketType?: MarketType;
  teamIconUrl?: string;
  subCategoryIconUrl?: string;
  size?: number;
  mirrored?: boolean;
  className?: string;
};

const resolveIconUrl = ({
  marketType,
  teamIconUrl,
  subCategoryIconUrl,
}: Required<Pick<MarketIconProps, 'marketType'>> &
  Pick<MarketIconProps, 'teamIconUrl' | 'subCategoryIconUrl'>): string | undefined =>
  match(marketType)
    .with('total', () => subCategoryIconUrl)
    .with('moneyline', 'spread', 'future_team_champion', 'player_prop', () => teamIconUrl)
    .exhaustive();

function RemoteIcon({
  iconUrl,
  size = DEFAULT_SIZE,
  mirrored = false,
  className,
}: RemoteIconProps) {
  const { classes, cx } = useStyles({ size });
  const iconClassName = cx(classes.icon, mirrored && classes.mirrored, className);

  if (!iconUrl) {
    return <div className={iconClassName} aria-hidden="true" />;
  }

  return <Image src={iconUrl} alt="" width={size} height={size} className={iconClassName} />;
}

function MarketIconComponent({
  marketType = 'moneyline',
  teamIconUrl,
  subCategoryIconUrl,
  size,
  mirrored,
  className,
}: MarketIconProps) {
  return (
    <RemoteIcon
      iconUrl={resolveIconUrl({ marketType, teamIconUrl, subCategoryIconUrl })}
      size={size}
      mirrored={mirrored}
      className={className}
    />
  );
}

export const MarketIcon = memo(MarketIconComponent);

MarketIcon.displayName = 'MarketIcon';

export const MarketTeamIcon = memo(
  ({
    teamIconUrl,
    size,
    mirrored,
    className,
  }: Pick<MarketIconProps, 'teamIconUrl' | 'size' | 'mirrored' | 'className'>) => (
    <MarketIcon
      marketType="moneyline"
      teamIconUrl={teamIconUrl}
      size={size}
      mirrored={mirrored}
      className={className}
    />
  )
);

MarketTeamIcon.displayName = 'MarketTeamIcon';

export const MarketGroupIcon = memo(({ iconUrl, size, className }: MarketGroupIconProps) => {
  if (!iconUrl) {
    return null;
  }

  return <RemoteIcon iconUrl={iconUrl} size={size} className={className} />;
});

MarketGroupIcon.displayName = 'MarketGroupIcon';
